/**
 * ClientReportEditor Component
 * Editable client-facing report with AI-generated insights
 */
import {
  AlertCircle,
  AlertTriangle,
  FileText,
  Lightbulb,
  Pencil,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSettingsStore } from "../../../stores/settingsStore";
import {
  Achievement,
  AIRecommendation,
  ChannelCharacteristic,
  Improvement,
} from "../types";
import ChannelHighlights from "./ChannelHighlights";

interface ClientReportEditorProps {
  achievements?: Achievement[];
  improvements?: Improvement[];
  recommendations?: AIRecommendation[];
  channelCharacteristics?: ChannelCharacteristic[] | undefined;
  canEdit?: boolean;
  /** Per-channel fee context from responseData.metrics.fee_context */
  feeContext?:
    | Record<string, { fee_included: boolean; fee_percent: number }>
    | undefined;
  onAchievementCommentChange?: (index: number, comment: string) => void;
  onImprovementCommentChange?: (index: number, comment: string) => void;
  onRecommendationCommentChange?: (index: number, comment: string) => void;
  onDeleteAchievement?: (index: number) => void;
  onDeleteImprovement?: (index: number) => void;
  onDeleteRecommendation?: (index: number) => void;
  onAddAchievement?: (item: {
    title: string;
    metric: string;
    current_value: string;
    comparison?: string;
    context?: string;
    summary?: string;
  }) => void;
  onAddImprovement?: (item: {
    title: string;
    area: string;
    metric: string;
    severity: "High" | "Medium" | "Low";
  }) => void;
  onAddRecommendation?: (item: {
    title: string;
    description: string;
    expected_outcomes?: string[];
    impact: "high" | "medium" | "low";
  }) => void;
  onUpdateAchievement?: (index: number, item: Achievement) => void;
  onUpdateImprovement?: (index: number, item: Improvement) => void;
  onUpdateRecommendation?: (
    index: number,
    item: {
      title: string;
      description: string;
      expected_outcomes?: string[];
      impact: "high" | "medium" | "low";
    },
  ) => void;
  reportStatus?:
    | "PENDING"
    | "PROCESSING"
    | "DRAFT"
    | "SUBMITTED"
    | "PUBLISHED"
    | "FAILED";
  onSubmitReport?: () => void;
  isSubmitting?: boolean;
  /** True when the AI service returned a service/network error — shows an amber warning */
  aiInsightsMissing?: boolean;
  /** True when the AI service had no BQ data for this period — shows a neutral info note */
  aiInsightsNoData?: boolean;
}

export default function ClientReportEditor({
  achievements = [],
  improvements = [],
  recommendations = [],
  channelCharacteristics,
  canEdit = false,
  feeContext,
  onAchievementCommentChange: _onAchievementCommentChange,
  onImprovementCommentChange: _onImprovementCommentChange,
  onRecommendationCommentChange: _onRecommendationCommentChange,
  onDeleteAchievement,
  onDeleteImprovement,
  onDeleteRecommendation,
  onAddAchievement,
  onAddImprovement,
  onAddRecommendation,
  onUpdateAchievement,
  onUpdateImprovement,
  onUpdateRecommendation,
  reportStatus,
  onSubmitReport,
  isSubmitting = false,
  aiInsightsMissing = false,
  aiInsightsNoData = false,
}: ClientReportEditorProps) {
  const { t } = useTranslation();
  const darkMode = useSettingsStore((state) => state.darkMode);

  const getStatusBadgeClass = () => {
    if (reportStatus === "DRAFT") {
      return darkMode
        ? "bg-blue-500/20 text-blue-300"
        : "bg-blue-100 text-blue-700";
    }
    if (reportStatus === "SUBMITTED") {
      return darkMode
        ? "bg-purple-500/20 text-purple-300"
        : "bg-purple-100 text-purple-700";
    }
    if (reportStatus === "PUBLISHED") {
      return darkMode
        ? "bg-green-500/20 text-green-300"
        : "bg-green-100 text-green-700";
    }
    if (reportStatus === "PENDING" || reportStatus === "PROCESSING") {
      return darkMode
        ? "bg-yellow-500/20 text-yellow-300"
        : "bg-yellow-100 text-yellow-700";
    }
    if (reportStatus === "FAILED") {
      return darkMode
        ? "bg-red-500/20 text-red-300"
        : "bg-red-100 text-red-700";
    }
    return darkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-700";
  };

  const [newAchievement, setNewAchievement] = useState({
    title: "",
    metric: "",
    current_value: "",
    summary: "",
  });
  const [newImprovement, setNewImprovement] = useState<{
    title: string;
    area: string;
    metric: string;
    severity: "High" | "Medium" | "Low";
    summary: string;
  }>({
    title: "",
    area: "",
    metric: "",
    severity: "Medium",
    summary: "",
  });
  const [newRecommendation, setNewRecommendation] = useState<{
    title: string;
    description: string;
    expected_outcomes: string;
    impact: "high" | "medium" | "low";
  }>({
    title: "",
    description: "",
    expected_outcomes: "",
    impact: "medium",
  });

  const addAchievement = () => {
    if (!newAchievement.title.trim() || !newAchievement.metric.trim()) return;
    onAddAchievement?.({
      title: newAchievement.title.trim(),
      metric: newAchievement.metric.trim(),
      current_value: newAchievement.current_value.trim(),
      ...(newAchievement.summary.trim() !== ""
        ? { summary: newAchievement.summary.trim() }
        : {}),
    });
    setNewAchievement({
      title: "",
      metric: "",
      current_value: "",
      summary: "",
    });
    setShowAddKeyResult(false);
  };

  const addImprovement = () => {
    if (!newImprovement.title.trim() || !newImprovement.area.trim()) return;
    onAddImprovement?.({
      title: newImprovement.title.trim(),
      area: newImprovement.area.trim(),
      metric: newImprovement.metric.trim(),
      severity: newImprovement.severity,
      ...(newImprovement.summary.trim() !== ""
        ? { summary: newImprovement.summary.trim() }
        : {}),
    });
    setNewImprovement({
      title: "",
      area: "",
      metric: "",
      severity: "Medium",
      summary: "",
    });
    setShowAddImprovement(false);
  };

  const addRecommendation = () => {
    if (!newRecommendation.title.trim()) return;
    const outcomes = newRecommendation.expected_outcomes
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    onAddRecommendation?.({
      title: newRecommendation.title.trim(),
      description: newRecommendation.description.trim(),
      ...(outcomes.length > 0 ? { expected_outcomes: outcomes } : {}),
      impact: newRecommendation.impact,
    });
    setNewRecommendation({
      title: "",
      description: "",
      expected_outcomes: "",
      impact: "medium",
    });
    setShowAddRecommendation(false);
  };

  const [showAddKeyResult, setShowAddKeyResult] = useState(false);
  const [showAddImprovement, setShowAddImprovement] = useState(false);
  const [showAddRecommendation, setShowAddRecommendation] = useState(false);

  const [editingAchievementIndex, setEditingAchievementIndex] = useState<
    number | null
  >(null);
  const [editAchievementDraft, setEditAchievementDraft] =
    useState<Achievement | null>(null);
  const [editingImprovementIndex, setEditingImprovementIndex] = useState<
    number | null
  >(null);
  const [editImprovementDraft, setEditImprovementDraft] =
    useState<Improvement | null>(null);
  const [editingRecommendationIndex, setEditingRecommendationIndex] = useState<
    number | null
  >(null);
  const [editRecommendationDraft, setEditRecommendationDraft] = useState<{
    title: string;
    description: string;
    expected_outcomes_text: string;
    impact: "high" | "medium" | "low";
  } | null>(null);

  return (
    <div
      className={`rounded-3xl p-4 sm:p-6 border ${
        darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <h3
            className={`text-base sm:text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
          >
            {t("reports.clientFacingReportEditing")}
          </h3>
          {reportStatus && (
            <span
              className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusBadgeClass()}`}
            >
              {t(`reports.status.${reportStatus.toLowerCase()}`) ||
                reportStatus}
            </span>
          )}
        </div>
        <button
          onClick={onSubmitReport}
          disabled={!canEdit || isSubmitting}
          className="px-4 sm:px-6 py-2.5 bg-[var(--brand-secondary)] text-white rounded-xl font-medium hover:bg-[var(--brand-secondary-hover)] transition-all flex items-center gap-2 text-sm sm:text-base justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          {t("reports.generateClientReport")}
        </button>
      </div>

      {/* Amber warning — AI service was down or unreachable */}
      {aiInsightsMissing && (
        <div
          className={`mb-6 flex items-start gap-3 rounded-xl border p-4 ${
            darkMode
              ? "bg-yellow-900/20 border-yellow-700/50"
              : "bg-yellow-50 border-yellow-200"
          }`}
        >
          <AlertTriangle
            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              darkMode ? "text-yellow-400" : "text-yellow-600"
            }`}
          />
          <div>
            <p
              className={`text-sm font-semibold mb-1 ${
                darkMode ? "text-yellow-300" : "text-yellow-800"
              }`}
            >
              {t("reports.aiInsightsMissingTitle")}
            </p>
            <p
              className={`text-xs leading-relaxed ${
                darkMode ? "text-yellow-400/80" : "text-yellow-700"
              }`}
            >
              {t("reports.aiInsightsMissingMessage")}
            </p>
          </div>
        </div>
      )}

      {/* Neutral info note — no analytics data available for this period */}
      {!aiInsightsMissing && aiInsightsNoData && (
        <div
          className={`mb-6 flex items-start gap-3 rounded-xl border p-4 ${
            darkMode
              ? "bg-gray-700/40 border-gray-600"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <AlertCircle
            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              darkMode ? "text-gray-400" : "text-gray-500"
            }`}
          />
          <div>
            <p
              className={`text-sm font-semibold mb-1 ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              {t("reports.aiInsightsNoDataTitle")}
            </p>
            <p
              className={`text-xs leading-relaxed ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {t("reports.aiInsightsNoDataMessage")}
            </p>
          </div>
        </div>
      )}

      <div
        className={`space-y-0 divide-y ${darkMode ? "divide-gray-700" : "divide-gray-200"}`}
      >
        {/* Section 1: Edit Results and Insights (Key Results / Achievements) */}
        <div className="pb-10">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp
              className={`w-5 h-5 ${darkMode ? "text-[var(--brand-primary)]" : "text-[var(--brand-primary)]"}`}
            />
            <h4
              className={`text-base font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {t("reports.keyResultsTitle")}
            </h4>
          </div>

          {achievements.length === 0 ? (
            <div
              className={`text-center py-6 rounded-xl border-2 border-dashed ${darkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <p
                className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {t("reports.noAchievementsAvailable") ||
                  "No key results available"}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {achievements.map((achievement, index) => {
                const isEditingAchievement = editingAchievementIndex === index;
                return (
                  <div
                    key={index}
                    className={`border-l-4 ${index % 2 === 0 ? "border-[var(--brand-primary)]" : "border-[var(--brand-secondary)]"} pl-4 rounded-r-xl p-4 ${
                      darkMode
                        ? "bg-gray-700/50"
                        : index % 2 === 0
                          ? "bg-[#f8fcfc]"
                          : "bg-[#fefaf5]"
                    }`}
                  >
                    {isEditingAchievement && editAchievementDraft ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1 md:col-span-2">
                            <input
                              value={editAchievementDraft.title}
                              onChange={(e) =>
                                setEditAchievementDraft((prev) =>
                                  prev
                                    ? { ...prev, title: e.target.value }
                                    : prev,
                                )
                              }
                              placeholder="Title"
                              className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                            />
                            <p
                              className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              {t("reports.keyResultTitleHint") ||
                                "e.g. Organic Search Conversions Soar by 32.84%"}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <input
                              value={editAchievementDraft.metric}
                              onChange={(e) =>
                                setEditAchievementDraft((prev) =>
                                  prev
                                    ? { ...prev, metric: e.target.value }
                                    : prev,
                                )
                              }
                              placeholder="Metric"
                              className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                            />
                            <p
                              className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              {t("reports.keyResultMetricHint") ||
                                "e.g. Organic Search Conversions, Revenue, Sessions"}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <input
                              value={editAchievementDraft.current_value}
                              onChange={(e) =>
                                setEditAchievementDraft((prev) =>
                                  prev
                                    ? { ...prev, current_value: e.target.value }
                                    : prev,
                                )
                              }
                              placeholder="Current value"
                              className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                            />
                            <p
                              className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              {t("reports.keyResultValueHint") ||
                                "e.g. 480, +32.84%, ¥1,200,000"}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <textarea
                            value={editAchievementDraft.summary ?? ""}
                            onChange={(e) =>
                              setEditAchievementDraft((prev) =>
                                prev
                                  ? { ...prev, summary: e.target.value }
                                  : prev,
                              )
                            }
                            placeholder={
                              t("reports.keyResultSummaryPlaceholder") ||
                              "Summary (optional)"
                            }
                            className={`w-full px-3 py-2 rounded-xl border text-sm resize-none ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"}`}
                            rows={3}
                          />
                          <p
                            className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {t("reports.keyResultSummaryHint") ||
                              "e.g. Increased 32% vs. previous period, driven by improved content performance"}
                          </p>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => {
                              setEditingAchievementIndex(null);
                              setEditAchievementDraft(null);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${darkMode ? "bg-gray-600 text-gray-300 hover:bg-gray-500" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              if (editAchievementDraft) {
                                onUpdateAchievement?.(
                                  index,
                                  editAchievementDraft,
                                );
                                setEditingAchievementIndex(null);
                                setEditAchievementDraft(null);
                              }
                            }}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--brand-primary)] text-white hover:opacity-90"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <span
                          className={`flex-shrink-0 w-6 h-6 ${index % 2 === 0 ? "bg-[var(--brand-primary)]" : "bg-[var(--brand-secondary)]"} text-white rounded-full flex items-center justify-center text-xs font-bold`}
                        >
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <h5
                            className={`text-sm font-semibold mb-1 ${darkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {achievement.title}
                          </h5>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span
                              className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {achievement.metric}
                            </span>
                            {achievement.current_value && (
                              <span
                                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                style={{
                                  background:
                                    index % 2 === 0
                                      ? "color-mix(in srgb, var(--brand-primary) 15%, transparent)"
                                      : "color-mix(in srgb, var(--brand-secondary) 15%, transparent)",
                                  color:
                                    index % 2 === 0
                                      ? "var(--brand-primary)"
                                      : "var(--brand-secondary)",
                                }}
                              >
                                {achievement.current_value}
                              </span>
                            )}
                            {achievement.comparison && (
                              <span
                                className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                — {achievement.comparison}
                              </span>
                            )}
                          </div>
                          {achievement.summary && (
                            <p
                              className={`text-xs leading-relaxed mt-1.5 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {achievement.summary}
                            </p>
                          )}
                        </div>
                        {/* Edit Button */}
                        <button
                          onClick={() => {
                            setEditingAchievementIndex(index);
                            setEditAchievementDraft({ ...achievement });
                          }}
                          disabled={!canEdit}
                          className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${
                            !canEdit ? "opacity-40 cursor-not-allowed" : ""
                          } ${
                            darkMode
                              ? "hover:bg-blue-900/30 text-blue-400 hover:text-blue-300"
                              : "hover:bg-blue-50 text-blue-500 hover:text-blue-600"
                          }`}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {/* Delete Button */}
                        <button
                          onClick={() => onDeleteAchievement?.(index)}
                          disabled={!canEdit}
                          className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${
                            !canEdit ? "opacity-40 cursor-not-allowed" : ""
                          } ${
                            darkMode
                              ? "hover:bg-red-900/30 text-red-400 hover:text-red-300"
                              : "hover:bg-red-50 text-red-600 hover:text-red-700"
                          }`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {canEdit && showAddKeyResult && (
            <div
              className={`mt-5 rounded-xl border p-3 sm:p-4 ${darkMode ? "border-gray-700 bg-gray-700/20" : "border-gray-200 bg-gray-50"}`}
            >
              <p
                className={`text-xs font-semibold mb-3 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {t("reports.addKeyResult") || "Add Key Result"}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2 space-y-1">
                  <input
                    value={newAchievement.title}
                    onChange={(e) =>
                      setNewAchievement((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    placeholder={
                      t("reports.keyResultTitlePlaceholder") || "Title"
                    }
                    className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                  />
                  <p
                    className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {t("reports.keyResultTitleHint") ||
                      "e.g. Organic Search Conversions Soar by 32.84%"}
                  </p>
                </div>
                <div className="space-y-1">
                  <input
                    value={newAchievement.metric}
                    onChange={(e) =>
                      setNewAchievement((prev) => ({
                        ...prev,
                        metric: e.target.value,
                      }))
                    }
                    placeholder={
                      t("reports.keyResultMetricPlaceholder") || "Metric"
                    }
                    className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                  />
                  <p
                    className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {t("reports.keyResultMetricHint") ||
                      "e.g. Organic Search Conversions, Revenue, Sessions"}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <input
                    value={newAchievement.current_value}
                    onChange={(e) =>
                      setNewAchievement((prev) => ({
                        ...prev,
                        current_value: e.target.value,
                      }))
                    }
                    placeholder={
                      t("reports.keyResultValuePlaceholder") || "Current value"
                    }
                    className={`px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                  />
                  <p
                    className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {t("reports.keyResultValueHint") ||
                      "e.g. 480, +32.84%, ¥1,200,000"}
                  </p>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                <textarea
                  value={newAchievement.summary}
                  onChange={(e) =>
                    setNewAchievement((prev) => ({
                      ...prev,
                      summary: e.target.value,
                    }))
                  }
                  placeholder={
                    t("reports.keyResultSummaryPlaceholder") ||
                    "Summary (optional)"
                  }
                  className={`w-full px-3 py-2 rounded-xl border text-sm resize-none ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"}`}
                  rows={3}
                />
                <p
                  className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  {t("reports.keyResultSummaryHint") ||
                    "e.g. Increased 32% vs. previous period, driven by improved content performance"}
                </p>
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  onClick={addAchievement}
                  className="px-4 py-2 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-medium hover:opacity-90"
                >
                  {t("reports.add") || "Add"}
                </button>
              </div>
            </div>
          )}

          {canEdit && (
            <button
              onClick={() => setShowAddKeyResult((prev) => !prev)}
              className={`mt-4 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-colors text-[var(--brand-primary)] ${
                showAddKeyResult
                  ? darkMode
                    ? "bg-[var(--brand-primary)]/20"
                    : "bg-[var(--brand-primary)]/10"
                  : darkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-100 hover:bg-gray-200"
              }`}
            >
              {showAddKeyResult
                ? t("reports.cancel") || "Cancel"
                : t("reports.addNewInsights") || "+ Add new Insights"}
            </button>
          )}
        </div>

        {/* Section 2: Areas of Improvement */}
        <div className="pt-10 pb-10">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle
              className={`w-5 h-5 ${darkMode ? "text-[var(--brand-secondary)]" : "text-[var(--brand-secondary)]"}`}
            />
            <h4
              className={`text-base font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {t("reports.areasOfImprovementTitle")}
            </h4>
          </div>

          {improvements.length === 0 ? (
            <div
              className={`text-center py-6 rounded-xl border-2 border-dashed ${darkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <p
                className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {t("reports.noImprovementsAvailable") ||
                  "No areas of improvement identified"}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {improvements.map((improvement, index) => {
                const isEditingImprovement = editingImprovementIndex === index;
                const severityColor =
                  improvement.severity === "High"
                    ? "text-red-500"
                    : improvement.severity === "Medium"
                      ? "text-orange-500"
                      : "text-yellow-500";

                return (
                  <div
                    key={index}
                    className={`border-l-4 ${index % 2 === 0 ? "border-[var(--brand-primary)]" : "border-[var(--brand-secondary)]"} pl-4 rounded-r-xl p-4 ${
                      darkMode
                        ? "bg-gray-700/50"
                        : index % 2 === 0
                          ? "bg-[#f8fcfc]"
                          : "bg-[#fefaf5]"
                    }`}
                  >
                    {isEditingImprovement && editImprovementDraft ? (
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <input
                            value={editImprovementDraft.title}
                            onChange={(e) =>
                              setEditImprovementDraft((prev) =>
                                prev
                                  ? { ...prev, title: e.target.value }
                                  : prev,
                              )
                            }
                            placeholder="Title"
                            className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                          />
                          <p
                            className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {t("reports.improvementTitleHint") ||
                              "e.g. Universal 100% Bounce Rate Across All Channels"}
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <input
                              value={editImprovementDraft.area}
                              onChange={(e) =>
                                setEditImprovementDraft((prev) =>
                                  prev
                                    ? { ...prev, area: e.target.value }
                                    : prev,
                                )
                              }
                              placeholder="Area"
                              className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                            />
                            <p
                              className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              {t("reports.improvementAreaHint") ||
                                "e.g. Website Analytics / Data Integrity"}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <input
                              value={editImprovementDraft.metric}
                              onChange={(e) =>
                                setEditImprovementDraft((prev) =>
                                  prev
                                    ? { ...prev, metric: e.target.value }
                                    : prev,
                                )
                              }
                              placeholder="Metric details"
                              className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                            />
                            <p
                              className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              {t("reports.improvementMetricHint") ||
                                "e.g. Bounce Rate"}
                            </p>
                          </div>
                        </div>
                        {/* Severity pill radio group */}
                        <div>
                          <p
                            className={`text-xs font-medium mb-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            {t("reports.severity") || "Severity"}
                          </p>
                          <div className="flex gap-2">
                            {(["High", "Medium", "Low"] as const).map(
                              (level) => (
                                <label key={level} className="cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`edit-improvement-severity-${index}`}
                                    value={level}
                                    checked={
                                      editImprovementDraft.severity === level
                                    }
                                    onChange={() =>
                                      setEditImprovementDraft((prev) =>
                                        prev
                                          ? { ...prev, severity: level }
                                          : prev,
                                      )
                                    }
                                    className="sr-only"
                                  />
                                  <span
                                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                                      editImprovementDraft.severity === level
                                        ? level === "High"
                                          ? "bg-red-500 border-red-500 text-white"
                                          : level === "Medium"
                                            ? "bg-orange-400 border-orange-400 text-white"
                                            : "bg-yellow-400 border-yellow-400 text-white"
                                        : darkMode
                                          ? "bg-transparent border-gray-600 text-gray-400 hover:border-gray-500"
                                          : "bg-transparent border-gray-300 text-gray-500 hover:border-gray-400"
                                    }`}
                                  >
                                    {level}
                                  </span>
                                </label>
                              ),
                            )}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <textarea
                            value={editImprovementDraft.summary ?? ""}
                            onChange={(e) =>
                              setEditImprovementDraft((prev) =>
                                prev
                                  ? { ...prev, summary: e.target.value }
                                  : prev,
                              )
                            }
                            rows={3}
                            placeholder={
                              t("reports.improvementSummaryPlaceholder") ||
                              "Summary (optional)"
                            }
                            className={`w-full px-3 py-2 rounded-xl border text-sm resize-none ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"}`}
                          />
                          <p
                            className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {t("reports.improvementSummaryHint") ||
                              "e.g. Every channel reports 100% bounce rate — likely a tracking issue rather than actual user behavior"}
                          </p>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => {
                              setEditingImprovementIndex(null);
                              setEditImprovementDraft(null);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${darkMode ? "bg-gray-600 text-gray-300 hover:bg-gray-500" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              if (editImprovementDraft) {
                                onUpdateImprovement?.(
                                  index,
                                  editImprovementDraft,
                                );
                                setEditingImprovementIndex(null);
                                setEditImprovementDraft(null);
                              }
                            }}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--brand-secondary)] text-white hover:opacity-90"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <span
                          className={`flex-shrink-0 w-6 h-6 ${index % 2 === 0 ? "bg-[var(--brand-primary)]" : "bg-[var(--brand-secondary)]"} text-white rounded-full flex items-center justify-center text-xs font-bold`}
                        >
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5
                              className={`text-sm font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {improvement.title}
                            </h5>
                            <span
                              className={`text-xs font-bold px-2 py-0.5 rounded ${severityColor} bg-opacity-10`}
                            >
                              {improvement.severity}
                            </span>
                          </div>
                          <div
                            className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                          >
                            <span className="font-medium">
                              {improvement.area}:{" "}
                            </span>
                            <span>{improvement.metric}</span>
                          </div>
                          {improvement.summary && (
                            <p
                              className={`text-xs leading-relaxed ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {improvement.summary}
                            </p>
                          )}
                        </div>
                        {/* Edit Button */}
                        <button
                          onClick={() => {
                            setEditingImprovementIndex(index);
                            setEditImprovementDraft({ ...improvement });
                          }}
                          disabled={!canEdit}
                          className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${
                            !canEdit ? "opacity-40 cursor-not-allowed" : ""
                          } ${
                            darkMode
                              ? "hover:bg-blue-900/30 text-blue-400 hover:text-blue-300"
                              : "hover:bg-blue-50 text-blue-500 hover:text-blue-600"
                          }`}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {/* Delete Button */}
                        <button
                          onClick={() => onDeleteImprovement?.(index)}
                          disabled={!canEdit}
                          className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${
                            !canEdit ? "opacity-40 cursor-not-allowed" : ""
                          } ${
                            darkMode
                              ? "hover:bg-red-900/30 text-red-400 hover:text-red-300"
                              : "hover:bg-red-50 text-red-600 hover:text-red-700"
                          }`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {canEdit && showAddImprovement && (
            <div
              className={`mt-5 rounded-xl border p-3 sm:p-4 ${darkMode ? "border-gray-700 bg-gray-700/20" : "border-gray-200 bg-gray-50"}`}
            >
              <p
                className={`text-xs font-semibold mb-3 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {t("reports.addImprovement") || "Add Area of Improvement"}
              </p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <input
                    value={newImprovement.title}
                    onChange={(e) =>
                      setNewImprovement((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    placeholder={
                      t("reports.improvementTitlePlaceholder") || "Title"
                    }
                    className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                  />
                  <p
                    className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {t("reports.improvementTitleHint") ||
                      "e.g. Universal 100% Bounce Rate Across All Channels"}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <input
                      value={newImprovement.area}
                      onChange={(e) =>
                        setNewImprovement((prev) => ({
                          ...prev,
                          area: e.target.value,
                        }))
                      }
                      placeholder={
                        t("reports.improvementAreaPlaceholder") || "Area"
                      }
                      className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                    />
                    <p
                      className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      {t("reports.improvementAreaHint") ||
                        "e.g. Website Analytics / Data Integrity"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <input
                      value={newImprovement.metric}
                      onChange={(e) =>
                        setNewImprovement((prev) => ({
                          ...prev,
                          metric: e.target.value,
                        }))
                      }
                      placeholder={
                        t("reports.improvementMetricPlaceholder") ||
                        "Metric details"
                      }
                      className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                    />
                    <p
                      className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      {t("reports.improvementMetricHint") || "e.g. Bounce Rate"}
                    </p>
                  </div>
                </div>
                {/* Severity pill radio group */}
                <div>
                  <p
                    className={`text-xs font-medium mb-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {t("reports.severity") || "Severity"}
                  </p>
                  <div className="flex gap-2">
                    {(["High", "Medium", "Low"] as const).map((level) => (
                      <label key={level} className="cursor-pointer">
                        <input
                          type="radio"
                          name="new-improvement-severity"
                          value={level}
                          checked={newImprovement.severity === level}
                          onChange={() =>
                            setNewImprovement((prev) => ({
                              ...prev,
                              severity: level,
                            }))
                          }
                          className="sr-only"
                        />
                        <span
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                            newImprovement.severity === level
                              ? level === "High"
                                ? "bg-red-500 border-red-500 text-white"
                                : level === "Medium"
                                  ? "bg-orange-400 border-orange-400 text-white"
                                  : "bg-yellow-400 border-yellow-400 text-white"
                              : darkMode
                                ? "bg-transparent border-gray-600 text-gray-400 hover:border-gray-500"
                                : "bg-transparent border-gray-300 text-gray-500 hover:border-gray-400"
                          }`}
                        >
                          {level}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <textarea
                    value={newImprovement.summary}
                    onChange={(e) =>
                      setNewImprovement((prev) => ({
                        ...prev,
                        summary: e.target.value,
                      }))
                    }
                    rows={3}
                    placeholder={
                      t("reports.improvementSummaryPlaceholder") ||
                      "Summary (optional)"
                    }
                    className={`w-full px-3 py-2 rounded-xl border text-sm resize-none ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"}`}
                  />
                  <p
                    className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {t("reports.improvementSummaryHint") ||
                      "e.g. Every channel reports 100% bounce rate — likely a tracking issue rather than actual user behavior"}
                  </p>
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  onClick={addImprovement}
                  className="px-4 py-2 rounded-xl bg-[var(--brand-secondary)] text-white text-sm font-medium hover:opacity-90"
                >
                  {t("reports.add") || "Add"}
                </button>
              </div>
            </div>
          )}

          {canEdit && (
            <button
              onClick={() => setShowAddImprovement((prev) => !prev)}
              className={`mt-4 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-colors text-[var(--brand-primary)] ${
                showAddImprovement
                  ? darkMode
                    ? "bg-[var(--brand-primary)]/20"
                    : "bg-[var(--brand-primary)]/10"
                  : darkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-100 hover:bg-gray-200"
              }`}
            >
              {showAddImprovement
                ? t("reports.cancel") || "Cancel"
                : t("reports.addNewImprovement") || "+ Add Area of Improvement"}
            </button>
          )}
        </div>

        {/* Channel Highlights */}
        <div className="pt-10">
          <ChannelHighlights
            channelCharacteristics={channelCharacteristics}
            feeContext={feeContext}
          />
        </div>

        {/* Section 3: Edit Future Action and Proposals (Recommendations) */}
        <div className="pt-10">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb
              className={`w-5 h-5 ${darkMode ? "text-[var(--brand-accent)]" : "text-[var(--brand-accent)]"}`}
            />
            <h4
              className={`text-base font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
            >
              {t("reports.futureActionsTitle")}
            </h4>
          </div>

          {recommendations.length === 0 ? (
            <div
              className={`text-center py-6 rounded-xl border-2 border-dashed ${darkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <p
                className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {t("reports.noRecommendationsAvailable") ||
                  "No recommendations available"}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {recommendations.map((recommendation, index) => {
                const isEditingRecommendation =
                  editingRecommendationIndex === index;
                const impactColor =
                  recommendation.impact === "high"
                    ? "bg-green-500"
                    : recommendation.impact === "medium"
                      ? "bg-yellow-500"
                      : "bg-blue-500";

                return (
                  <div
                    key={recommendation.id || `recommendation-${index}`}
                    className={`border-l-4 ${index % 2 === 0 ? "border-[var(--brand-primary)]" : "border-[var(--brand-secondary)]"} pl-4 rounded-r-xl p-4 ${
                      darkMode
                        ? "bg-gray-700/50"
                        : index % 2 === 0
                          ? "bg-[#f8fcfc]"
                          : "bg-[#fefaf5]"
                    }`}
                  >
                    {isEditingRecommendation && editRecommendationDraft ? (
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <input
                            value={editRecommendationDraft.title}
                            onChange={(e) =>
                              setEditRecommendationDraft((prev) =>
                                prev
                                  ? { ...prev, title: e.target.value }
                                  : prev,
                              )
                            }
                            placeholder="Title"
                            className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                          />
                          <p
                            className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {t("reports.recommendationTitleHint") ||
                              "e.g. Fix Bounce Rate Tracking Implementation"}
                          </p>
                        </div>
                        {/* Impact pill radio group */}
                        <div>
                          <p
                            className={`text-xs font-medium mb-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            {t("reports.impact") || "Impact"}
                          </p>
                          <div className="flex gap-2">
                            {(["high", "medium", "low"] as const).map(
                              (level) => (
                                <label key={level} className="cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`edit-recommendation-impact-${index}`}
                                    value={level}
                                    checked={
                                      editRecommendationDraft.impact === level
                                    }
                                    onChange={() =>
                                      setEditRecommendationDraft((prev) =>
                                        prev
                                          ? { ...prev, impact: level }
                                          : prev,
                                      )
                                    }
                                    className="sr-only"
                                  />
                                  <span
                                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border transition-all capitalize ${
                                      editRecommendationDraft.impact === level
                                        ? level === "high"
                                          ? "bg-red-500 border-red-500 text-white"
                                          : level === "medium"
                                            ? "bg-orange-400 border-orange-400 text-white"
                                            : "bg-yellow-400 border-yellow-400 text-white"
                                        : darkMode
                                          ? "bg-transparent border-gray-600 text-gray-400 hover:border-gray-500"
                                          : "bg-transparent border-gray-300 text-gray-500 hover:border-gray-400"
                                    }`}
                                  >
                                    {level}
                                  </span>
                                </label>
                              ),
                            )}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <textarea
                            value={editRecommendationDraft.description}
                            onChange={(e) =>
                              setEditRecommendationDraft((prev) =>
                                prev
                                  ? { ...prev, description: e.target.value }
                                  : prev,
                              )
                            }
                            placeholder="Description"
                            className={`w-full px-3 py-2 rounded-xl border text-sm resize-none ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"}`}
                            rows={2}
                          />
                          <p
                            className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {t("reports.recommendationDescriptionHint") ||
                              "e.g. Audit Google Tag Manager setup to ensure scroll depth and interaction events are firing correctly"}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <textarea
                            value={
                              editRecommendationDraft.expected_outcomes_text
                            }
                            onChange={(e) =>
                              setEditRecommendationDraft((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      expected_outcomes_text: e.target.value,
                                    }
                                  : prev,
                              )
                            }
                            placeholder="Expected outcomes (one per line, optional)"
                            className={`w-full px-3 py-2 rounded-xl border text-sm resize-none ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"}`}
                            rows={2}
                          />
                          <p
                            className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {t("reports.recommendationOutcomesHint") ||
                              "One per line \u2014 e.g. Accurate engagement data / Actionable channel insights"}
                          </p>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => {
                              setEditingRecommendationIndex(null);
                              setEditRecommendationDraft(null);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${darkMode ? "bg-gray-600 text-gray-300 hover:bg-gray-500" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              if (editRecommendationDraft) {
                                onUpdateRecommendation?.(index, {
                                  title: editRecommendationDraft.title,
                                  description:
                                    editRecommendationDraft.description,
                                  expected_outcomes:
                                    editRecommendationDraft.expected_outcomes_text
                                      .split("\n")
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  impact: editRecommendationDraft.impact,
                                });
                                setEditingRecommendationIndex(null);
                                setEditRecommendationDraft(null);
                              }
                            }}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--brand-accent)] text-gray-900 hover:opacity-90"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <span
                          className={`flex-shrink-0 w-6 h-6 ${impactColor} text-white rounded-full flex items-center justify-center text-xs font-bold`}
                        >
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`text-xs font-bold px-2 py-0.5 rounded ${impactColor} text-white`}
                            >
                              {recommendation.impact === "high"
                                ? t("reports.highImpact")
                                : recommendation.impact === "medium"
                                  ? t("reports.mediumImpact")
                                  : t("reports.lowImpact")}
                            </span>
                          </div>
                          <h5
                            className={`text-sm font-semibold mb-1 ${darkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {recommendation.title}
                          </h5>
                          <p
                            className={`text-xs leading-relaxed ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {recommendation.description}
                          </p>
                          {recommendation.expected_outcomes &&
                            recommendation.expected_outcomes.length > 0 && (
                              <div className="mt-2">
                                <p
                                  className={`text-xs font-semibold mb-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  {t("reports.expectedImpact")}
                                </p>
                                <ul className="space-y-0.5">
                                  {recommendation.expected_outcomes.map(
                                    (outcome, oi) => (
                                      <li
                                        key={oi}
                                        className={`text-xs flex items-start gap-1.5 ${darkMode ? "text-gray-400" : "text-gray-500"}`}
                                      >
                                        <span
                                          className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full"
                                          style={{
                                            backgroundColor:
                                              index % 2 === 0
                                                ? "var(--brand-primary)"
                                                : "var(--brand-secondary)",
                                          }}
                                        />
                                        {outcome}
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            )}
                        </div>
                        {/* Edit Button */}
                        <button
                          onClick={() => {
                            setEditingRecommendationIndex(index);
                            setEditRecommendationDraft({
                              title: recommendation.title,
                              description: recommendation.description,
                              expected_outcomes_text: (
                                recommendation.expected_outcomes ?? []
                              ).join("\n"),
                              impact: recommendation.impact,
                            });
                          }}
                          disabled={!canEdit}
                          className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${
                            !canEdit ? "opacity-40 cursor-not-allowed" : ""
                          } ${
                            darkMode
                              ? "hover:bg-blue-900/30 text-blue-400 hover:text-blue-300"
                              : "hover:bg-blue-50 text-blue-500 hover:text-blue-600"
                          }`}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {/* Delete Button */}
                        <button
                          onClick={() => onDeleteRecommendation?.(index)}
                          disabled={!canEdit}
                          className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${
                            !canEdit ? "opacity-40 cursor-not-allowed" : ""
                          } ${
                            darkMode
                              ? "hover:bg-red-900/30 text-red-400 hover:text-red-300"
                              : "hover:bg-red-50 text-red-600 hover:text-red-700"
                          }`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {canEdit && showAddRecommendation && (
            <div
              className={`mt-5 rounded-xl border p-3 sm:p-4 ${darkMode ? "border-gray-700 bg-gray-700/20" : "border-gray-200 bg-gray-50"}`}
            >
              <p
                className={`text-xs font-semibold mb-3 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {t("reports.addRecommendation") ||
                  "Add Future Action / Proposal"}
              </p>
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-1">
                  <input
                    value={newRecommendation.title}
                    onChange={(e) =>
                      setNewRecommendation((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    placeholder={
                      t("reports.recommendationTitlePlaceholder") || "Title"
                    }
                    className={`w-full px-3 py-2 rounded-xl border text-sm ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200 text-gray-800"}`}
                  />
                  <p
                    className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {t("reports.recommendationTitleHint") ||
                      "e.g. Fix Bounce Rate Tracking Implementation"}
                  </p>
                </div>
                {/* Impact pill radio group */}
                <div>
                  <p
                    className={`text-xs font-medium mb-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {t("reports.impact") || "Impact"}
                  </p>
                  <div className="flex gap-2">
                    {(["high", "medium", "low"] as const).map((level) => (
                      <label key={level} className="cursor-pointer">
                        <input
                          type="radio"
                          name="new-recommendation-impact"
                          value={level}
                          checked={newRecommendation.impact === level}
                          onChange={() =>
                            setNewRecommendation((prev) => ({
                              ...prev,
                              impact: level,
                            }))
                          }
                          className="sr-only"
                        />
                        <span
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border transition-all capitalize ${
                            newRecommendation.impact === level
                              ? level === "high"
                                ? "bg-red-500 border-red-500 text-white"
                                : level === "medium"
                                  ? "bg-orange-400 border-orange-400 text-white"
                                  : "bg-yellow-400 border-yellow-400 text-white"
                              : darkMode
                                ? "bg-transparent border-gray-600 text-gray-400 hover:border-gray-500"
                                : "bg-transparent border-gray-300 text-gray-500 hover:border-gray-400"
                          }`}
                        >
                          {level}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <textarea
                    value={newRecommendation.description}
                    onChange={(e) =>
                      setNewRecommendation((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder={
                      t("reports.recommendationDescriptionPlaceholder") ||
                      "Description"
                    }
                    className={`w-full px-3 py-2 rounded-xl border text-sm resize-none ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"}`}
                    rows={2}
                  />
                  <p
                    className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {t("reports.recommendationDescriptionHint") ||
                      "e.g. Audit Google Tag Manager setup to ensure scroll depth and interaction events are firing correctly"}
                  </p>
                </div>
                <div className="space-y-1">
                  <textarea
                    value={newRecommendation.expected_outcomes}
                    onChange={(e) =>
                      setNewRecommendation((prev) => ({
                        ...prev,
                        expected_outcomes: e.target.value,
                      }))
                    }
                    placeholder={
                      t("reports.recommendationOutcomesPlaceholder") ||
                      "Expected outcomes (one per line, optional)"
                    }
                    className={`w-full px-3 py-2 rounded-xl border text-sm resize-none ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-800 placeholder-gray-400"}`}
                    rows={2}
                  />
                  <p
                    className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {t("reports.recommendationOutcomesHint") ||
                      "One per line \u2014 e.g. Accurate engagement data / Actionable channel insights"}
                  </p>
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  onClick={addRecommendation}
                  className="px-4 py-2 rounded-xl bg-[var(--brand-accent)] text-gray-900 text-sm font-medium hover:opacity-90"
                >
                  {t("reports.add") || "Add"}
                </button>
              </div>
            </div>
          )}

          {canEdit && (
            <button
              onClick={() => setShowAddRecommendation((prev) => !prev)}
              className={`mt-4 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-colors text-[var(--brand-primary)] ${
                showAddRecommendation
                  ? darkMode
                    ? "bg-[var(--brand-primary)]/20"
                    : "bg-[var(--brand-primary)]/10"
                  : darkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-100 hover:bg-gray-200"
              }`}
            >
              {showAddRecommendation
                ? t("reports.cancel") || "Cancel"
                : t("reports.addNewRecommendation") ||
                  "+ Add Future Action / Proposal"}
            </button>
          )}
        </div>
      </div>
      {/* end divide wrapper */}
    </div>
  );
}
